import React, { useState, useEffect, useMemo } from 'react';
import { getAllUsers, createUser, updateUser, deleteUser } from '../services/userService';
import {
  Avatar,
  Banner,
  Button,
  EmptyState,
  Field,
  FormSection,
  IconButton,
  Modal,
  Pagination,
  Pill,
  RowActions,
  SearchInput,
  SelectFilter,
  SkeletonRows,
  StatTiles,
  Tabs,
  Toast,
  formatDate,
  timeAgo,
  useConfirm,
  useFlash,
  usePagination
} from './AdminUI';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  phone: '',
  address: '',
  role: 'user'
};

const DAY_MS = 24 * 60 * 60 * 1000;

const readCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useFlash();
  const [confirm, confirmDialog] = useConfirm();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const currentUser = useMemo(readCurrentUser, []);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const isSelf = (user) =>
    Boolean(currentUser) &&
    ((currentUser.id && (user.id === currentUser.id || user._id === currentUser.id)) ||
      (currentUser.email && user.email === currentUser.email));

  const counts = useMemo(() => {
    const since = Date.now() - 30 * DAY_MS;
    return {
      admins: users.filter((u) => u.role === 'admin').length,
      customers: users.filter((u) => u.role !== 'admin').length,
      recent: users.filter((u) => new Date(u.createdAt).getTime() >= since).length,
      google: users.filter((u) => u.authProvider === 'google').length
    };
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = users.filter((u) => {
      if (roleFilter === 'admin' && u.role !== 'admin') return false;
      if (roleFilter === 'user' && u.role === 'admin') return false;
      if (!q) return true;
      return [u.name, u.email, u.phone, u.address].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
    const sorters = {
      newest: (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      oldest: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      az: (a, b) => (a.name || '').localeCompare(b.name || '')
    };
    return [...list].sort(sorters[sortBy]);
  }, [users, search, roleFilter, sortBy]);

  const pager = usePagination(filtered, 10, `${search}|${roleFilter}|${sortBy}`);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        role: formData.role || 'user'
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      Object.keys(userData).forEach((key) => {
        if (userData[key] === undefined) {
          delete userData[key];
        }
      });

      const userId = editingUser?.id || editingUser?._id;

      if (editingUser && userId) {
        await updateUser(userId, userData);
        setSuccess('User updated');
      } else {
        if (!formData.password) {
          throw new Error('Password is required for new users');
        }
        await createUser(userData);
        setSuccess('User created');
      }

      closeModal();
      loadUsers();
    } catch (err) {
      setFormError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormError('');
    setShowPassword(false);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      address: user.address || '',
      role: user.role || 'user'
    });
    setShowModal(true);
  };

  const handleDelete = (user) =>
    confirm({
      title: 'Delete this user?',
      message: 'Their account will be removed and they will no longer be able to sign in.',
      icon: 'fa-user-xmark',
      item: {
        media: <Avatar name={user.name || user.email} size="lg" tone={user.role === 'admin' ? 'navy' : 'soft'} />,
        title: user.name || 'Unnamed user',
        meta: `${user.email || 'No email'} · ${user.role === 'admin' ? 'Administrator' : 'Customer'}`
      },
      points: [
        'This action cannot be undone.',
        ...(user.role === 'admin' ? ['This person will lose access to the admin dashboard.'] : [])
      ],
      confirmLabel: 'Delete user',
      confirmIcon: 'fa-trash-can',
      busyLabel: 'Deleting…',
      onConfirm: async () => {
        setSaving(true);
        setError('');
        try {
          await deleteUser(user.id || user._id);
          setSuccess('User deleted');
          loadUsers();
        } catch (err) {
          setError(err.message || 'Failed to delete user');
        } finally {
          setSaving(false);
        }
      }
    });

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowPassword(false);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowPassword(false);
    setShowModal(true);
  };

  const stats = [
    { id: 'all', label: 'Total users', value: users.length, hint: `${counts.google} via Google sign-in`, icon: 'fa-users', tone: 'blue', filter: 'all' },
    { id: 'user', label: 'Customers', value: counts.customers, hint: 'Shop accounts', icon: 'fa-user', tone: 'teal', filter: 'user' },
    { id: 'admin', label: 'Administrators', value: counts.admins, hint: 'Dashboard access', icon: 'fa-user-shield', tone: 'indigo', filter: 'admin' },
    { id: 'recent', label: 'New this month', value: counts.recent, hint: 'Joined in last 30 days', icon: 'fa-user-plus', tone: 'green' }
  ];

  return (
    <div className="ax">
      <StatTiles items={stats} active={roleFilter} onSelect={setRoleFilter} />

      {error ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

      <div className="ax-card">
        <div className="ax-toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, phone…" label="Search users" />
          <Tabs
            value={roleFilter}
            onChange={setRoleFilter}
            label="Role"
            options={[
              { id: 'all', label: 'All', count: users.length },
              { id: 'user', label: 'Customers', count: counts.customers },
              { id: 'admin', label: 'Admins', count: counts.admins }
            ]}
          />
          <SelectFilter
            value={sortBy}
            onChange={setSortBy}
            icon="fa-arrow-down-wide-short"
            label="Sort users"
            options={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
              { value: 'az', label: 'Name A–Z' }
            ]}
          />
          <span className="ax-toolbar__grow" />
          <IconButton
            icon={loading ? 'fa-spinner fa-spin' : 'fa-rotate'}
            label="Refresh users"
            className="ax-icon-btn--outline"
            onClick={loadUsers}
            disabled={loading}
          />
          <Button variant="primary" icon="fa-user-plus" onClick={openCreateModal}>
            Add user
          </Button>
        </div>

        {loading && !users.length ? (
          <SkeletonRows cols={5} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="fa-users"
            title={users.length ? 'No users match' : 'No users yet'}
            text={users.length ? 'Try a different search or role filter.' : 'Create the first account to get started.'}
            action={
              users.length ? null : (
                <Button variant="primary" icon="fa-user-plus" onClick={openCreateModal}>
                  Add user
                </Button>
              )
            }
          />
        ) : (
          <>
            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th className="is-right">
                      <span className="ax-sr">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageItems.map((user) => {
                    const self = isSelf(user);
                    return (
                      <tr key={user.id || user._id} className="is-clickable" onClick={() => handleEdit(user)}>
                        <td>
                          <div className="ax-cell">
                            <Avatar name={user.name || user.email} tone={user.role === 'admin' ? 'navy' : 'soft'} />
                            <div className="ax-cell__text">
                              <strong>
                                {user.name || 'Unnamed user'}
                                {self ? <span className="ax-you">You</span> : null}
                              </strong>
                              <small>{user.email || '—'}</small>
                            </div>
                          </div>
                        </td>
                        <td className="is-nowrap">{user.phone || <span className="ax-sub">—</span>}</td>
                        <td>
                          <span className="ax-clamp" title={user.address || ''}>
                            {user.address || <span className="ax-sub">—</span>}
                          </span>
                        </td>
                        <td>
                          {user.role === 'admin' ? (
                            <Pill tone="indigo">Admin</Pill>
                          ) : (
                            <Pill tone="gray">Customer</Pill>
                          )}
                          {user.authProvider === 'google' ? (
                            <span className="ax-sub">
                              <i className="fab fa-google" aria-hidden="true" /> Google
                            </span>
                          ) : null}
                        </td>
                        <td className="is-nowrap">
                          <span className="ax-strong">{formatDate(user.createdAt)}</span>
                          <span className="ax-sub">{timeAgo(user.createdAt)}</span>
                        </td>
                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                          <RowActions
                            label={`Actions for ${user.name || user.email}`}
                            title={user.name || 'Unnamed user'}
                            subtitle={`${user.email || 'No email'} · ${user.role === 'admin' ? 'Administrator' : 'Customer'}`}
                            media={<Avatar name={user.name || user.email} size="lg" tone={user.role === 'admin' ? 'navy' : 'soft'} />}
                            actions={[
                              {
                                id: 'edit',
                                label: 'Edit user',
                                description: 'Change name, role, password or contact info',
                                icon: 'fa-pen',
                                onClick: () => handleEdit(user),
                              },
                              user.email
                                ? {
                                    id: 'email',
                                    label: 'Send email',
                                    description: user.email,
                                    icon: 'fa-envelope',
                                    onClick: () => {
                                      window.location.href = `mailto:${user.email}`;
                                    },
                                  }
                                : null,
                              user.phone
                                ? {
                                    id: 'call',
                                    label: 'Call user',
                                    description: user.phone,
                                    icon: 'fa-phone',
                                    onClick: () => {
                                      window.location.href = `tel:${user.phone}`;
                                    },
                                  }
                                : null,
                              {
                                id: 'delete',
                                label: 'Delete user',
                                description: self ? "You can't delete your own account" : 'Permanently remove this account',
                                icon: 'fa-trash-can',
                                tone: 'danger',
                                disabled: saving || self,
                                onClick: () => handleDelete(user),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination pager={pager} label="users" />
          </>
        )}
      </div>

      <Modal
        open={showModal}
        onClose={closeModal}
        as="form"
        onSubmit={handleSubmit}
        icon={editingUser ? 'fa-user-pen' : 'fa-user-plus'}
        title={editingUser ? 'Edit user' : 'New user'}
        subtitle={editingUser ? editingUser.email : 'Create a customer or administrator account'}
        footer={
          <>
            <Button onClick={closeModal}>Cancel</Button>
            <Button type="submit" variant="primary" icon="fa-check" loading={saving} disabled={saving}>
              {editingUser ? 'Save changes' : 'Create user'}
            </Button>
          </>
        }
      >
        {formError ? <Banner>{formError}</Banner> : null}

        <FormSection title="Account">
          <Field label="Full name" required htmlFor="uf-name">
            <input id="uf-name" className="ax-input" type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Jane Uwase" />
          </Field>
          <Field label="Email" required htmlFor="uf-email">
            <input id="uf-email" className="ax-input" type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="jane@example.com" />
          </Field>
          <Field
            label="Password"
            required={!editingUser}
            span={2}
            htmlFor="uf-password"
            hint={editingUser ? 'Leave blank to keep the current password.' : 'At least 6 characters.'}
          >
            <div className="ax-input-group">
              <input
                id="uf-password"
                className="ax-input"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
                placeholder={editingUser ? '••••••••' : 'Create a password'}
                minLength={editingUser ? undefined : 6}
                autoComplete="new-password"
              />
              <span className="ax-input-group__action">
                <IconButton
                  icon={showPassword ? 'fa-eye-slash' : 'fa-eye'}
                  label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                />
              </span>
            </div>
          </Field>
        </FormSection>

        <FormSection title="Access" text="Administrators can sign in to this dashboard.">
          <div className="ax-choice-grid">
            {[
              { id: 'user', icon: 'fa-user', title: 'Customer', text: 'Shop, checkout and track orders' },
              { id: 'admin', icon: 'fa-user-shield', title: 'Administrator', text: 'Full access to the admin console' }
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                className={`ax-choice ${formData.role === option.id ? 'is-active' : ''}`}
                onClick={() => setFormData((prev) => ({ ...prev, role: option.id }))}
                aria-pressed={formData.role === option.id}
              >
                <i className={`fas ${option.icon}`} aria-hidden="true" />
                <span>
                  <strong>{option.title}</strong>
                  <small>{option.text}</small>
                </span>
              </button>
            ))}
          </div>
        </FormSection>

        <FormSection title="Contact details">
          <Field label="Phone" htmlFor="uf-phone">
            <input id="uf-phone" className="ax-input" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+250 788 123 456" />
          </Field>
          <Field label="Address" htmlFor="uf-address">
            <input id="uf-address" className="ax-input" type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder="Street, district, city" />
          </Field>
        </FormSection>
      </Modal>

      <Toast message={success} />
      {confirmDialog}
    </div>
  );
}

export default UsersManagement;
